/**
 * Bulk worklists (feat/bulk-worklists): Accounts receivable (Billing &
 * insurance), Recall worklist (Patients) and Treatment plan worklist
 * (Clinical). Each list filters, totals, selects (row, page, or all N
 * matching) and runs mass actions: email / text through the composer,
 * follow-up tasks, CSV export, and paper statements for A/R.
 *
 * Sending is turned off until Settings > Automations & notifications allow
 * it (and texting has an approved A2P 10DLC registration): the composer's
 * last step then reads "Ready to send N messages. Sending is turned off".
 */
import { defineManifest } from '@velora/contracts';
import { defineWebModule } from '../../app/module.js';

export default defineWebModule({
  manifest: defineManifest({
    id: 'worklists',
    title: 'Worklists',
    version: '0.1.0',
    owner: 'WP4-BILLING',
    entitlementKey: null,
    permissions: ['money.view', 'patient.view', 'comms.send', 'comms.view'],
    routes: [
      {
        id: 'ar',
        path: '/billing/receivables',
        title: 'Accounts receivable',
        officeScope: 'location-or-all',
        permission: 'money.view',
        readsPhi: true,
      },
      {
        id: 'recall',
        path: '/worklists/recall',
        title: 'Recall worklist',
        officeScope: 'location-or-all',
        permission: 'patient.view',
        readsPhi: true,
      },
      {
        id: 'treatment',
        path: '/worklists/treatment',
        title: 'Treatment plan worklist',
        officeScope: 'location-or-all',
        permission: 'patient.view',
        readsPhi: true,
      },
    ],
    nav: [
      {
        label: 'Accounts receivable',
        group: 'revenue',
        routeId: 'ar',
        icon: 'circle-dollar-sign',
        order: 11,
        permission: 'money.view',
      },
      {
        label: 'Recall worklist',
        group: 'patients',
        routeId: 'recall',
        icon: 'list-checks',
        order: 36,
        permission: 'patient.view',
      },
      {
        label: 'Treatment worklist',
        group: 'clinical',
        routeId: 'treatment',
        icon: 'clipboard-list',
        order: 46,
        permission: 'patient.view',
      },
    ],
    commands: [
      {
        id: 'worklists.ar',
        title: 'Go to Accounts receivable',
        keywords: [
          'a/r',
          'ar',
          'receivables',
          'balances',
          'aging',
          'collections',
          'mass email',
          'pay link',
        ],
        routeId: 'ar',
        permission: 'money.view',
      },
      {
        id: 'worklists.recall',
        title: 'Go to Recall worklist',
        keywords: ['recall', 'hygiene', 'overdue', 'continuing care', 'mass text', 'mass email'],
        routeId: 'recall',
        permission: 'patient.view',
      },
      {
        id: 'worklists.treatment',
        title: 'Go to Treatment plan worklist',
        keywords: [
          'treatment plan',
          'unscheduled treatment',
          'crowns',
          'implants',
          'perio',
          'benefits',
        ],
        routeId: 'treatment',
        permission: 'patient.view',
      },
    ],
    sseEntityKeys: ['aging_snapshot', 'ledger_entry'],
    phiReadPoints: ['worklists.ar', 'worklists.recall', 'worklists.treatment'],
  }),
  pages: {
    ar: () => import('./ArPage.js'),
    recall: () => import('./RecallPage.js'),
    treatment: () => import('./TreatmentPage.js'),
  },
});
